import { enqueue, type EnqueueInput } from './outbox';
import type { OutboxOp } from './dexie';
import type { Lang, Message, MessageShadow, Uuid } from './types';

/**
 * Attaching a translation to a message that already went.
 *
 * A message written with signal carries its translated shadow from the start:
 * the phone calls `translate` and sends both halves at once. A message written
 * in a dead corner goes without one, because there was nobody to ask. Until
 * migration 018 there was no way back from that, and the shadow was lost for
 * good (reported in NEEDS-FROM-FOUNDATION.md).
 *
 * The rule is narrow, and it is the database that holds it:
 *
 *   only the person who wrote the message may attach its translation,
 *   only text_es and text_en may change, and
 *   a shadow that is already there is never rewritten.
 *
 * So there is nothing to check here beyond which column to fill. A screen
 * queues this like any other write and it lands with the next signal.
 */

export type AttachShadowInput = {
  /** The client_id the message was sent with, which is how the row is found. */
  clientId: Uuid;
  /** The language of the translation, not of the original. */
  lang: Lang;
  text: string;
};

/**
 * Which column a translation goes in, and nothing else. Pure, so the mapping is
 * tested rather than repeated in two screens.
 */
export function shadowFor(lang: Lang, text: string): MessageShadow {
  return lang === 'es' ? { text_es: text } : { text_en: text };
}

/** True when this message is still missing the shadow for that language. */
export function needsShadow(message: Pick<Message, 'text_es' | 'text_en'>, lang: Lang): boolean {
  return (lang === 'es' ? message.text_es : message.text_en) === null;
}

/**
 * Queues the update. It goes through the outbox like every other write, so it
 * survives the app being closed and cannot be sent twice in a way that matters:
 * the trigger refuses a second value anyway.
 */
export function attachMessageShadow(input: AttachShadowInput): Promise<OutboxOp> {
  return enqueue({
    table: 'messages',
    kind: 'update',
    payload: shadowFor(input.lang, input.text),
    match: { client_id: input.clientId },
  });
}

/**
 * The same late shadow, for a request (migration 022).
 *
 * A guest request written with no signal went to the island in its own
 * language only. The guest's phone may now attach the translation to a request
 * of its own stay, and a crew member to a report they filed, once each, and
 * nothing else about the row may change. A second translation arriving later
 * is quietly kept out by the database rather than refused, so the outbox never
 * parks it.
 */
export type ShadowTable = 'messages' | 'requests';

/** The outbox write that attaches a shadow. Pure, so a screen can hand it to any engine. */
export function shadowUpdate(table: ShadowTable, input: AttachShadowInput): EnqueueInput {
  return {
    table,
    kind: 'update',
    payload: shadowFor(input.lang, input.text),
    match: { client_id: input.clientId },
  };
}

/** Queues the request's shadow, through the outbox like every other write. */
export function attachRequestShadow(input: AttachShadowInput): Promise<OutboxOp> {
  return enqueue(shadowUpdate('requests', input));
}

/**
 * True when a shadow for this row is already waiting in the outbox, so a
 * screen that looks again before the queue drains does not translate twice.
 */
export function shadowQueued(
  ops: ReadonlyArray<Pick<OutboxOp, 'table' | 'kind' | 'match'>>,
  table: ShadowTable,
  clientId: Uuid,
): boolean {
  return ops.some((op) => op.table === table && op.kind === 'update' && op.match?.client_id === clientId);
}
